import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Pencil, ArrowLeft, Upload } from "lucide-react";
import { TruncatedText } from "@/components/ui/truncated-text";
import type { ABTest, ContentType, TestOutcome } from "@/types/ab-tests";
import { getTestSourceLabel, getWinningHypotheses } from "@/utils/ab-test-utils";
import { ActivityLogTimeline } from "../ActivityLogTimeline";
import { EditTestSectionDrawer, type EditableSection } from "../EditTestSectionDrawer";
import { UploadResultsModal } from "../UploadResultsModal";
import { toast } from "@/hooks/use-toast";
import { getBulletIndex } from "@/utils/ab-test-utils";
import { TestStatusBadge } from "./TestStatusBadge";
import { TestMetaSummary } from "./TestMetaSummary";
import { SignalsSection } from "./SignalsSection";
import { AmazonMetricsTable } from "./AmazonMetricsTable";
import { VariantCards } from "./VariantCards";
import { ConfigurationMeta } from "./ConfigurationMeta";

interface ViewTestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  test: ABTest;
  productId?: number;
  brandId?: number | null;
  onEdit?: (test: ABTest) => void;
  onUploadResults?: (testId: string, file: File, outcome?: TestOutcome, variantBSwitchDate?: string) => Promise<void>;
  onEditConfiguration?: (test: ABTest) => void;
  onBack?: () => void;
  onSavePatch?: (testId: string, patch: Partial<ABTest>) => void;
}

export function ViewTestModal({
  open,
  onOpenChange,
  test,
  productId,
  brandId,
  onUploadResults,
  onEditConfiguration,
  onBack,
  onSavePatch,
}: ViewTestModalProps) {
  const [overrides, setOverrides] = useState<Partial<ABTest>>({});
  const [editingSection, setEditingSection] = useState<EditableSection | null>(null);
  const [uploadResultsOpen, setUploadResultsOpen] = useState(false);

  useEffect(() => {
    setOverrides({});
  }, [test.id, open]);

  const merged = { ...test, ...overrides } as ABTest;
  const targetPhrase = merged.targetKeyword || merged.targetCluster || "\u2014";
  const isBulletSet = merged.contentType === "bullet-set";

  const testedFields: ContentType[] = [];
  if (merged.scopeFields && merged.scopeFields.length > 0) {
    testedFields.push(...merged.scopeFields);
  } else {
    if (merged.variantA.title || merged.variantB.title) testedFields.push("title");
    if (merged.variantA.bullets && merged.variantA.bullets.length > 0) {
      if (isBulletSet) {
        merged.variantA.bullets.forEach((_, idx) => {
          testedFields.push(`bullet-${idx + 1}` as ContentType);
        });
      } else {
        testedFields.push(merged.contentType);
      }
    }
    if (testedFields.length === 0) testedFields.push(merged.contentType);
  }

  const showIntent = merged.intentShift.from && merged.intentShift.to;

  const getFieldContent = (variant: typeof merged.variantA, field: ContentType): string => {
    if (field === "title") return variant.title || "\u2014";
    const bulletFields = testedFields.filter((f) => f.startsWith("bullet-"));
    // Stored bullets are compact and in scope order, one per bullet field ('' = slot the product lacks).
    if (variant.bullets && variant.bullets.length === bulletFields.length) {
      return variant.bullets[bulletFields.indexOf(field)] || "\u2014";
    }
    // Legacy / uploaded tests: the bullet list does not match the scope.
    const idx = getBulletIndex(field);
    if (idx !== null && variant.bullets && variant.bullets[idx]) return variant.bullets[idx];
    if (variant.bullets) {
      const bulletFieldIdx = bulletFields.indexOf(field);
      if (bulletFieldIdx >= 0 && variant.bullets[bulletFieldIdx]) return variant.bullets[bulletFieldIdx];
    }
    return "\u2014";
  };

  const isCompleted = !!merged.outcome;
  const winningHypotheses = getWinningHypotheses(merged);
  const sourceLabel = getTestSourceLabel(merged.source);

  const testName =
    merged.name && merged.name.trim().length > 0 && !merged.name.startsWith("Draft ")
      ? merged.name
      : merged.testType === "intent"
        ? merged.intentShift.to
        : merged.testType === "keyword"
          ? merged.targetKeyword || merged.targetCluster || "\u2014"
          : merged.testType === "custom"
            ? merged.customAxes && merged.customAxes.length > 0
              ? merged.customAxes.map((a) => a[0].toUpperCase() + a.slice(1)).join(" + ")
              : "Multi-axis"
            : merged.intentShift.to;

  const copyVariantB = () => {
    const parts: string[] = [];
    if (merged.variantB.title) parts.push(merged.variantB.title);
    merged.variantB.bullets?.filter(Boolean).forEach((b) => parts.push(b));
    if (parts.length === 0) return;
    void navigator.clipboard.writeText(parts.join("\n\n"));
    toast({ title: "Copied", description: "Variant B content copied to clipboard." });
  };

  const handleDrawerSave = (patch: Partial<ABTest>) => {
    setOverrides((prev) => ({ ...prev, ...patch }));
    onSavePatch?.(test.id, patch);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[640px] bg-card border-border p-0 flex flex-col gap-0 overflow-hidden"
        >
          {onBack && (
            <div className="px-4 pt-3 pb-0">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>
          )}

          <SheetHeader className={`px-6 ${onBack ? "pt-2" : "pt-6"} pb-4 border-b border-border space-y-3`}>
            <SheetTitle asChild>
              <div className="flex items-center gap-4 min-w-0">
                <h2 className="text-2xl font-semibold min-w-0">
                  <TruncatedText>{testName || "Test"}</TruncatedText>
                </h2>
                <TestStatusBadge status={merged.status} />
              </div>
            </SheetTitle>
            <SheetDescription asChild>
              <TestMetaSummary
                createdBy={merged.createdBy}
                startedAt={merged.startedAt}
                stoppedAt={merged.stoppedAt}
              />
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-8">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xl font-semibold text-foreground">Configuration</h3>
                {!isCompleted && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (onEditConfiguration) {
                        onEditConfiguration(merged);
                        onOpenChange(false);
                      } else {
                        setEditingSection("configuration");
                      }
                    }}
                    className="gap-1.5"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit configuration
                  </Button>
                )}
              </div>

              <ConfigurationMeta
                sourceLabel={sourceLabel}
                testedFields={testedFields}
                targetPhrase={targetPhrase}
                sourceDetail={merged.sourceDetail}
                hypothesis={merged.hypothesis}
              />

              <VariantCards
                variantA={merged.variantA}
                variantB={merged.variantB}
                testedFields={testedFields}
                showIntent={showIntent}
                intentFrom={merged.intentShift.from}
                intentTo={merged.intentShift.to}
                getFieldContent={getFieldContent}
                onCopyVariantB={copyVariantB}
              />
            </div>

            <div className="space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xl font-semibold text-foreground">Results</h3>
                <Button variant="outline" size="sm" onClick={() => { if (onUploadResults && !merged.amazonMetrics) { setUploadResultsOpen(true); } else { setEditingSection("results"); } }} className="gap-1.5">
                  <Upload className="h-3.5 w-3.5" />
                  {merged.amazonMetrics ? "Edit results" : "Upload results"}
                </Button>
              </div>
              {isCompleted && merged.amazonMetrics ? (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Amazon Experiment Results</p>
                  <AmazonMetricsTable metrics={merged.amazonMetrics} />
                </>
              ) : (
                <p className="text-sm text-muted-foreground">No experiment results uploaded yet.</p>
              )}
            </div>

            <div className="space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xl font-semibold text-foreground">Action</h3>
                <Button variant="outline" size="sm" onClick={() => setEditingSection("action")} className="gap-1.5">
                  <Pencil className="h-3.5 w-3.5" />
                  {merged.outcome ? "Edit action" : "Add action"}
                </Button>
              </div>
              {merged.outcome === "variant" && merged.variantBSwitchDate ? (
                <div>
                  <p className="text-sm font-medium text-foreground">Change submitted — Variant B applied</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(merged.variantBSwitchDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </p>
                </div>
              ) : merged.outcome === "control" ? (
                <p className="text-sm font-medium text-foreground">Keep Variant A — no listing change</p>
              ) : merged.outcome === "variant" ? (
                <p className="text-sm text-muted-foreground">Variant B won — no change submitted yet</p>
              ) : (
                <p className="text-sm text-muted-foreground">No action recorded yet.</p>
              )}
            </div>

            <SignalsSection isCompleted={isCompleted} winningHypotheses={winningHypotheses} />

            {merged.activityLog && merged.activityLog.length > 0 && (
              <ActivityLogTimeline
                entries={merged.activityLog}
                showHeader={true}
                productId={productId}
                brandId={brandId ?? undefined}
                testId={test.id}
              />
            )}
          </div>

          <SheetFooter className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 sm:justify-end">
            <Button onClick={() => onOpenChange(false)}>Close</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <EditTestSectionDrawer
        open={editingSection !== null}
        onOpenChange={(v) => { if (!v) setEditingSection(null); }}
        test={merged}
        section={editingSection}
        onSave={handleDrawerSave}
      />

      {onUploadResults && (
        <UploadResultsModal
          open={uploadResultsOpen}
          onOpenChange={setUploadResultsOpen}
          testId={test.id}
          testName={test.name}
          onUpload={onUploadResults}
        />
      )}
    </>
  );
}
